// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for reversing asset disposal (unscrap).
 * Provides functionality to undo the scrapping of an asset,
 * returning it to active service and clearing disposal information.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { assetSchema } from '@/asset/api/types';
import { z } from 'zod';

/**
 * Schema for validating the API response when unscapping an asset.
 * Transforms the response to provide a more intuitive structure with
 * the asset data separated from metadata.
 */
const responseSchema = z
  .object({ data: assetSchema, metadata: responseMetadataSchema })
  .transform(({ data, ...response }) => ({ ...response, asset: data }));

/**
 * Reverses the disposal (scrapping) of an asset.
 * This function handles the API call to restore a scrapped asset and validates
 * the response data using Zod schemas. It removes disposal information and
 * returns the asset to active service.
 *
 * @param key - Unique identifier of the asset to unscrap
 * @returns Promise resolving to the updated asset data and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function unscrapAsset(key: string) {
  const url = `/v1/asset/${key}/unscrap`;
  const response = await api.post(url);

  return parseResponse(responseSchema, response.data, {
    schemaName: 'UnscrapAsset',
    endpoint: url,
  });
}
